import { useRef, useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Reset kata sandi oleh Admin UFP dari halaman Keaktifan Akun (POST /admin/keaktifan/akun/{id}/reset-kata-sandi).
// Konfirmasi menyebut nama & prodi (tanpa email). Kata sandi sementara hanya hidup di state dialog ini: tampil
// SEKALI, dibuang saat dialog ditutup; tidak pernah ke localStorage, URL, atau console.
export const teksCakupanAkun = (akun) => akun?.prodi || ROLE_LABEL[akun?.peran] || "-";

export default function ResetSandiUfpDialog({ akun, onClose }) {
    const [proses, setProses] = useState(false);
    const [hasil, setHasil] = useState(null); // { nama, cakupan, sandi }
    // Penjaga klik ganda yg langsung berlaku (state `proses` baru terbaca setelah render ulang).
    const berjalan = useRef(false);

    const reset = async () => {
        if (berjalan.current) return;
        berjalan.current = true;
        setProses(true);
        try {
            const { data } = await api.post(`/admin/keaktifan/akun/${akun.id}/reset-kata-sandi`);
            setHasil({ nama: akun.nama, cakupan: teksCakupanAkun(akun), sandi: data.password_sementara });
            onClose();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
        berjalan.current = false;
        setProses(false);
    };

    const salin = async () => {
        try { await navigator.clipboard.writeText(hasil.sandi); toast.success("Disalin"); }
        catch { toast.error("Gagal menyalin. Salin manual saja."); }
    };

    return (
        <>
            <AlertDialog open={!!akun} onOpenChange={(o) => !o && !proses && onClose()}>
                <AlertDialogContent data-testid="reset-ufp-konfirmasi">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset kata sandi akun {akun?.nama} ({teksCakupanAkun(akun)})?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Akun ini akan keluar dari semua sesi dan wajib mengganti kata sandi saat login berikutnya.
                            Kata sandi sementara ditampilkan SEKALI kepada Anda.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={proses}>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="reset-ufp-ya"
                            disabled={proses}
                            aria-busy={proses}
                            className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                            onClick={(e) => { e.preventDefault(); reset(); }}
                        >
                            {proses ? "Memproses..." : "Reset Kata Sandi"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!hasil} onOpenChange={(o) => !o && setHasil(null)}>
                <DialogContent data-testid="reset-ufp-hasil">
                    <DialogHeader>
                        <DialogTitle>Kata sandi sementara</DialogTitle>
                        <DialogDescription>Untuk akun <b>{hasil?.nama}</b> ({hasil?.cakupan}):</DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <code className="flex-1 font-mono text-base break-all text-slate-900" data-testid="reset-ufp-sandi">{hasil?.sandi}</code>
                        <Button size="sm" variant="ghost" onClick={salin} title="Salin kata sandi" aria-label="Salin kata sandi"
                            data-testid="reset-ufp-salin"><Copy className="w-4 h-4" /></Button>
                    </div>
                    <p className="text-[12px] text-amber-700" data-testid="reset-ufp-peringatan">
                        Sampaikan lewat jalur aman; tampil sekali; tidak bisa dilihat lagi.
                    </p>
                    <DialogFooter>
                        <Button onClick={() => setHasil(null)} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="reset-ufp-selesai">
                            Selesai
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
